import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { Formik, Field, ErrorMessage, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import recurso_banner from './../../assets/img/recurso_banner.png';
import RecursoViewModel from '../../viewmodel/RecursoViewModel';
import { useLocation } from 'react-router-dom';
import { publicApi } from '../../api/';
import BackendErrorMessage from '../../components/BackendErrorMessage';

const formatCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const unformatCPF = (cpf: string) => {
    return cpf.replace(/[.-]/g, '');
};

// Função para verificar se o CPF é válido
const isValidCPF = (cpf: string) => {
    const unformattedCPF = unformatCPF(cpf);

    // Verifica se o CPF tem 11 dígitos ou é uma sequência de números repetidos
    if (unformattedCPF.length !== 11 || /^(\d)\1+$/.test(unformattedCPF)) return false;

    // Calcula os dois dígitos verificadores
    let sum = 0;
    let remainder;

    // Primeiro dígito verificador
    for (let i = 1; i <= 9; i++) {
        sum += parseInt(unformattedCPF.substring(i - 1, i)) * (11 - i);
    }

    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(unformattedCPF.substring(9, 10))) return false;

    // Segundo dígito verificador
    sum = 0;
    for (let i = 1; i <= 10; i++) {
        sum += parseInt(unformattedCPF.substring(i - 1, i)) * (12 - i);
    }

    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(unformattedCPF.substring(10, 11))) return false;

    return true;
};

const FormularioRecurso: React.FC = () => {
    // Recupera o argumento do cache se houver
    const cachedArgument = localStorage.getItem('argument') || '';
    const [tokenValido, setTokenValido] = useState(true)

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [candidatoInfo, setCandidatoInfo] = useState({
        argument: cachedArgument,
        candidatoBancaId: '',
        declaration: false,
        cpf: '',
        passport: '',
        nome: '',
        nomeSocial: '',
        email: '',
    });

    useEffect(() => {
        const send = async () => {
            // Validação do token antes de fazer a requisição
            if (!token || token.trim() === '') {
                toast.error('Token de acesso inválido ou não fornecido. Verifique o link de acesso.');
                setTokenValido(false);
                return;
            }

            try {
                const formData = new FormData();
                formData.append('token', token);

                const { data } = await publicApi.post('/emails/candidatos', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                console.log(data);

                const { cpf, passport, ...filteredData } = data;

                setCandidatoInfo({ ...candidatoInfo, ...filteredData });
            } catch (error) {
                console.error('Erro ao validar token:', error);
                const errorMessage = BackendErrorMessage(error) || 'Erro ao validar token';
                toast.error(errorMessage);
                setTokenValido(false);
            }
        };
        send();
    }, [token, setCandidatoInfo]);

    const validationSchema = Yup.object({
        argument: Yup.string().required('Este campo é obrigatório.'),
        declaration: Yup.boolean().oneOf([true], 'Você deve confirmar a declaração.'),
        cpf: Yup.string().test('isValidCPF', 'CPF inválido.', function (value) {
            // Se houver CPF preenchido, verifica se é válido; se não houver, não gera erro aqui
            if (value && value.trim() !== '') {
                return isValidCPF(value);
            }
            return true;
        }),
        passport: Yup.string(),
    }).test('cpfOrPassport', 'Preencha pelo menos um dos campos: CPF ou Passaporte.', (values) => {
        return (!!values?.cpf && values.cpf.trim() !== '') || (!!values?.passport && values.passport.trim() !== '');
    });

    const handleSubmit = async (values: { argument: string; declaration: boolean; cpf: string; passport: string }) => {
        const formattedCPF = values.cpf ? formatCPF(values.cpf) : '';

        try {
            await RecursoViewModel.criarRecurso(
                values.argument,
                candidatoInfo.candidatoBancaId,
                formattedCPF,
                values.passport, // O valor do passaporte é incluído na chamada ao backend
                token || ''
            );
            localStorage.removeItem('argument');
            toast.success('Pedido de Recurso realizado com sucesso', {
                autoClose: false,
                theme: 'colored',
                style: {
                    color: '#155724',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    fontWeight: '500'
                }
            });
            setIsSubmitted(true);
        } catch (error) {
            console.error('Erro ao criar recurso:', error);
            const errorMessage = BackendErrorMessage(error) || 'Erro ao criar recurso';
            toast.error(errorMessage, {
                autoClose: false,
                theme: 'colored',
            });
        }
    };

    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <img src={recurso_banner} alt="logo" className="mb-4 img-fluid" style={{ marginTop: '-26px' }} />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >

                        <h1 className="mb-4">Interposição de Recursos</h1>
                        <span
                            style={{
                                marginBottom: '16px',
                                border: '1px solid gray',
                                borderRadius: '12px',
                            }}
                        >
                            <div style={{ margin: '8px' }}>CAVU - {new Date().getFullYear()}</div>
                        </span>
                    </div>

                    <Alert variant="info">
                        <p>
                            Conforme previsto na Resolução GR-074/2020 e Resolução GR-031/2022, de 28/07/2022, preencha
                            todos os campos solicitados neste Formulário.
                        </p>
                        <p>
                            A análise do pedido de Recurso será feita via análise da gravação da Heteroidentificação já
                            realizada pela Banca Virtual, mas desta vez por uma Banca Recursal, composta por cinco
                            membros diferentes daqueles que compuseram a Banca Virtual realizada via plataforma Google
                            Meet, apontando uma nova deliberação de VALIDADA ou NÃO VALIDADA à condição autodeclarada
                            pelo(a) candidato(a).
                        </p>
                    </Alert>
                    <Alert variant="danger">
                        <p>
                            Lembramos que <strong>o prazo para interposição de recurso segue o período informado no e-mail de resultado</strong>,{' '}
                            <u>contado do envio do endereço eletrônico</u> contendo o resultado da deliberação
                            havida pela Comissão.
                        </p>
                        <p>
                            Interposições realizadas <strong>após este prazo não serão consideradas</strong>.
                        </p>
                    </Alert>

                    <Formik
                        initialValues={candidatoInfo}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({ values, setFieldValue, handleChange }) => (
                            <FormikForm>
                                <Form.Group className="mb-3">
                                    <Field
                                        type="checkbox"
                                        name="declaration"
                                        id="declaration"
                                        className="form-check-input"
                                        disabled={isSubmitted || !tokenValido}
                                        style={{ marginRight: '5px' }}
                                    />
                                    <label htmlFor="declaration" className="form-check-label">
                                        Declaro estar ciente da Resolução GR-074/2020 e do prazo informado no e-mail
                                        para interposição do Recurso.
                                    </label>
                                    <div>
                                        <a
                                            href="https://www.pg.unicamp.br/norma/21795/1"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Acesse a Resolução GR-074/2020
                                        </a>
                                    </div>
                                    <ErrorMessage name="declaration" component="div" className="text-danger" />
                                </Form.Group>

                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nome:</Form.Label>
                                            <Form.Control value={candidatoInfo.nomeSocial || candidatoInfo.nome} disabled />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email:</Form.Label>
                                            <Form.Control value={candidatoInfo.email} disabled />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Field
                                        name="argument"
                                        as="textarea"
                                        rows={6} // Tamanho aumentado
                                        className="form-control"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            handleChange(e);
                                            setFieldValue('argument', e.target.value);
                                            localStorage.setItem('argument', e.target.value); // Cache no localStorage
                                        }}
                                        disabled={isSubmitted || !tokenValido}
                                        style={{ width: '100%' }} // Ocupar toda a largura da div
                                    />
                                    <Form.Text className="text-muted">
                                        Apresente os argumentos com os quais contesta a decisão da banca.*
                                    </Form.Text>
                                    <ErrorMessage name="argument" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>CPF do Candidato (ou de seu Responsável**):</Form.Label>
                                    <Field
                                        name="cpf"
                                        as={Form.Control}
                                        value={values.cpf}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const rawValue = e.target.value.replace(/\D/g, '');
                                            setFieldValue('cpf', formatCPF(rawValue));
                                        }}
                                        disabled={!values.argument || isSubmitted || !tokenValido} // Desabilitado até que o argumento seja preenchido
                                    />
                                    <ErrorMessage name="cpf" component="div" className="text-danger" />
                                </Form.Group>                                
                                {/* Novo campo Passaporte */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Passaporte do Candidato (ou de seu Responsável**): <span style={{ color: 'gray' }}>(Caso não tenham CPF)</span></Form.Label>
                                    <Field
                                        name="passport"
                                        as={Form.Control}
                                        value={values.passport}
                                        onChange={handleChange}
                                        disabled={!values.argument || isSubmitted || !tokenValido}
                                    />
                                    <ErrorMessage name="passport" component="div" className="text-danger" />
                                </Form.Group>
                                <p className="text-muted">
                                    *Uma cópia deste documento será enviada para o endereço de e-mail fornecido.
                                </p>
                                <p className="text-muted">
                                    **Candidato menor de idade com Responsável registrado previamente.
                                </p>

                                <div
                                    className="mb-3"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Button
                                        size="lg"
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={
                                            !values.declaration ||
                                            !values.argument ||
                                            (!values.cpf && !values.passport) ||
                                            isSubmitted ||
                                            !tokenValido
                                        }
                                    >
                                        Enviar Recurso
                                    </Button>
                                </div>
                            </FormikForm>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};

export default FormularioRecurso;
